import React, { useState, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { ArrowLeft, Play, Pause, Volume2, VolumeX, ChevronRight, ChevronLeft, Heart } from 'lucide-react';
import { PrimaryButton, SecondaryButton, IconButton } from '../components/brand/BrandButton';
import { Heading, BodyText, SmallText } from '../components/brand/BrandTypography';

const API_URL = process.env.REACT_APP_BACKEND_URL;

/**
 * Somatic Experiencing / Body Psychotherapy Tool
 * Helps process emotions through body awareness
 * Based on Peter Levine's Somatic Experiencing principles
 */

const SOMATIC_EXERCISES = [
    {
        id: 'pendulation',
        name: 'Pendulation',
        description: 'Move between areas of comfort and discomfort to build resilience',
        duration: '5-10 min',
        icon: '🌊',
        glassVariant: 'glass-card-sage',
        steps: [
            {
                instruction: "Find a comfortable position. Take a few breaths to settle.",
                guidance: "There's no right way to do this. Just notice what's here."
            },
            {
                instruction: "Scan your body and find a place that feels neutral or pleasant. Maybe your hands, your feet, or somewhere else.",
                guidance: "This is your 'resource' — a place of relative ease."
            },
            {
                instruction: "Spend a moment here. Notice the sensations. Temperature, texture, weight.",
                guidance: "Let yourself rest in this place of comfort."
            },
            {
                instruction: "Now, gently bring attention to a place that holds some tension or discomfort. Don't choose the most intense area.",
                guidance: "Just a 3 or 4 on a scale of 1-10. We're not diving into trauma."
            },
            {
                instruction: "Notice what's there. Don't try to fix it. Just observe for a few breaths.",
                guidance: "You might notice the tension shift, change, or stay the same. All of that is okay."
            },
            {
                instruction: "Now return to your resource — that place of comfort you found earlier.",
                guidance: "Feel the difference. Let your nervous system register safety."
            },
            {
                instruction: "Continue this gentle pendulation: resource → discomfort → resource",
                guidance: "This teaches your nervous system that it can move through difficulty and return to safety."
            },
            {
                instruction: "End by resting in your resource. Take a few deep breaths.",
                guidance: "You've just practiced building resilience. Well done."
            }
        ]
    },
    {
        id: 'titration',
        name: 'Titration',
        description: 'Process difficult emotions in small, manageable doses',
        duration: '10-15 min',
        icon: '💧',
        glassVariant: 'glass-card-blue',
        steps: [
            {
                instruction: "Ground yourself first. Feel your feet on the floor. Notice your breath.",
                guidance: "Create a foundation of safety before we begin."
            },
            {
                instruction: "Think of something mildly stressful — not your biggest worry, just something small.",
                guidance: "Like a 2 or 3 on a stress scale. A minor annoyance."
            },
            {
                instruction: "Notice where this shows up in your body. Shoulders? Stomach? Chest?",
                guidance: "Just observe. Don't try to change anything."
            },
            {
                instruction: "Describe the sensation to yourself: tight, heavy, buzzy, warm, cold?",
                guidance: "Naming helps your brain process the experience."
            },
            {
                instruction: "Now, imagine the sensation as water in a cup. We're going to pour out just a tiny drop.",
                guidance: "Exhale slowly, and imagine releasing just 1% of that tension."
            },
            {
                instruction: "Notice any shift. It might be subtle — a slight softening, a change in temperature.",
                guidance: "Or nothing might change. That's okay too."
            },
            {
                instruction: "Continue with small releases. Don't try to empty the cup all at once.",
                guidance: "Titration means taking it slowly. Small doses are easier for your nervous system."
            },
            {
                instruction: "When ready, let go of the stressful thought. Return to your breath.",
                guidance: "You've practiced processing emotion in a manageable way."
            }
        ]
    },
    {
        id: 'container',
        name: 'Containment',
        description: 'Create a mental container for overwhelming feelings',
        duration: '5-8 min',
        icon: '📦',
        glassVariant: 'glass-card-lavender',
        steps: [
            {
                instruction: "Close your eyes if comfortable. Take three deep breaths.",
                guidance: "This exercise helps when emotions feel too big to handle right now."
            },
            {
                instruction: "Imagine a container. It could be a box, a chest, a vault — whatever feels right.",
                guidance: "Make it as sturdy as you need. It can hold anything."
            },
            {
                instruction: "Notice the details: What's it made of? What color? How big?",
                guidance: "The more vivid, the more effective."
            },
            {
                instruction: "Now, think of the overwhelming feeling or thought you want to contain for now.",
                guidance: "You're not getting rid of it — just putting it somewhere safe until you're ready."
            },
            {
                instruction: "Imagine placing that feeling or thought into your container.",
                guidance: "Watch it go in. The container holds it securely."
            },
            {
                instruction: "Close the container. Lock it if you want. It's safe there.",
                guidance: "You can come back to it when you have support or more capacity."
            },
            {
                instruction: "Take a breath. Notice if you feel any lighter or more spacious.",
                guidance: "The feeling isn't gone — it's just held for you until the right time."
            },
            {
                instruction: "Open your eyes when ready. Know that your container is always available to you.",
                guidance: "This is a skill you can use anytime emotions feel unmanageable."
            }
        ]
    },
    {
        id: 'discharge',
        name: 'Discharge & Release',
        description: 'Release stored tension through gentle movement',
        duration: '5-10 min',
        icon: '✨',
        glassVariant: 'glass-card-rose',
        steps: [
            {
                instruction: "Stand if possible, or sit upright. Feel your body's weight.",
                guidance: "We're going to help your body release tension naturally."
            },
            {
                instruction: "Start by shaking your hands gently. Let them be loose and floppy.",
                guidance: "Animals shake after stress to discharge adrenaline. We can too."
            },
            {
                instruction: "Let the shake travel up your arms. Loose, gentle movements.",
                guidance: "No forcing. Let your body find its natural rhythm."
            },
            {
                instruction: "If it feels right, let your shoulders bounce. Maybe your whole body.",
                guidance: "This might feel silly. That's okay. Your body knows what it needs."
            },
            {
                instruction: "Add some sighing breaths. Deep inhale, long exhale with sound.",
                guidance: "Sound helps release. Sigh, hum, or make gentle noise."
            },
            {
                instruction: "Let the movement slow naturally when your body is ready.",
                guidance: "Don't force it to stop. Let it wind down."
            },
            {
                instruction: "Stand or sit still. Notice the tingling, the aliveness in your body.",
                guidance: "This is your nervous system resetting."
            },
            {
                instruction: "Take a few deep breaths. Feel your feet on the ground.",
                guidance: "You've just helped your body complete a stress cycle."
            }
        ]
    }
];

const SomaticExperiencing = () => {
    const navigate = useNavigate();
    const [selectedExercise, setSelectedExercise] = useState(null);
    const [currentStep, setCurrentStep] = useState(0);
    const [audioEnabled, setAudioEnabled] = useState(true);
    const [isLoadingAudio, setIsLoadingAudio] = useState(false);
    const audioRef = useRef(null);

    const exercise = selectedExercise ? SOMATIC_EXERCISES.find(e => e.id === selectedExercise) : null;
    const step = exercise ? exercise.steps[currentStep] : null;

    // Generate audio for current step
    const generateAudio = async () => {
        if (!audioEnabled || !step) return;
        
        setIsLoadingAudio(true);
        try {
            const response = await fetch(`${API_URL}/api/tts/generate`, {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({
                    text: `${step.instruction} ... ${step.guidance}`,
                    voice: 'ballad',  // British, light, calm, gentle
                    speed: 0.8
                })
            });
            
            if (response.ok) {
                const data = await response.json();
                if (audioRef.current) {
                    audioRef.current.src = `data:audio/mp3;base64,${data.audio}`;
                    audioRef.current.play();
                }
            }
        } catch (error) {
            console.error('Audio generation failed:', error);
        } finally {
            setIsLoadingAudio(false);
        }
    };

    const handleSelectExercise = (id) => {
        setSelectedExercise(id);
        setCurrentStep(0);
    };

    const handleNext = () => {
        if (currentStep < exercise.steps.length - 1) {
            setCurrentStep(currentStep + 1);
            if (audioEnabled) generateAudio();
        }
    };

    const handlePrev = () => {
        if (currentStep > 0) {
            setCurrentStep(currentStep - 1);
        }
    };

    const handleBack = () => {
        if (selectedExercise) {
            setSelectedExercise(null);
            setCurrentStep(0);
            if (audioRef.current) {
                audioRef.current.pause();
                audioRef.current.src = '';
            }
        } else {
            navigate(-1);
        }
    };

    // Exercise selection screen
    if (!selectedExercise) {
        return (
            <div className="min-h-screen bg-[#FBF6EE]">
                <header className="sticky top-0 z-40 glass-header p-4">
                    <div className="flex items-center gap-3">
                        <IconButton onClick={() => navigate(-1)} data-testid="back-btn">
                            <ArrowLeft className="w-5 h-5" />
                        </IconButton>
                        <div>
                            <Heading level={3} className="text-[#00695C]">
                                Somatic Practices
                            </Heading>
                            <SmallText className="text-[#1D6B6B]">
                                Body-based emotional processing
                            </SmallText>
                        </div>
                    </div>
                </header>

                <main className="max-w-md mx-auto px-4 py-6 space-y-4">
                    <BodyText className="text-[#2A8585] leading-relaxed px-2">
                        These practices help you process emotions through your body. 
                        They're based on Somatic Experiencing® principles — working with 
                        your nervous system gently and at your own pace.
                    </BodyText>

                    {SOMATIC_EXERCISES.map((ex) => (
                        <button
                            key={ex.id}
                            onClick={() => handleSelectExercise(ex.id)}
                            className={`w-full ${ex.glassVariant || 'glass-card'} rounded-2xl p-5 text-left card-hover transition-all hover:shadow-md`}
                            data-testid={`exercise-${ex.id}`}
                        >
                            <div className="flex items-start gap-4">
                                <div className="w-12 h-12 rounded-full bg-white/60 flex items-center justify-center text-2xl">
                                    {ex.icon}
                                </div>
                                <div className="flex-1">
                                    <Heading level={4} className="text-[#00695C] mb-1">
                                        {ex.name}
                                    </Heading>
                                    <BodyText className="text-[#2A8585] mb-2">
                                        {ex.description}
                                    </BodyText>
                                    <SmallText className="text-[#80CBC4]">
                                        {ex.duration}
                                    </SmallText>
                                </div>
                                <ChevronRight className="w-5 h-5 text-[#80CBC4]" />
                            </div>
                        </button>
                    ))}

                    <div className="bg-[#FFF8E1] rounded-2xl p-4 mt-6">
                        <div className="flex items-start gap-3">
                            <Heart className="w-5 h-5 text-[#F9A825] flex-shrink-0 mt-0.5" />
                            <SmallText className="text-[#F57F17]">
                                <strong>Note:</strong> These practices are not a substitute for therapy. 
                                If you're processing trauma, please work with a trained somatic therapist.
                            </SmallText>
                        </div>
                    </div>
                </main>
            </div>
        );
    }

    // Exercise detail screen
    return (
        <div className="min-h-screen bg-[#FBF6EE]">
            <audio ref={audioRef} />
            
            <header className="sticky top-0 z-40 glass-header p-4">
                <div className="flex items-center justify-between">
                    <IconButton onClick={handleBack} data-testid="back-btn">
                        <ArrowLeft className="w-5 h-5" />
                    </IconButton>
                    <SmallText className="text-[#1D6B6B]">{exercise.name}</SmallText>
                    <IconButton 
                        onClick={() => setAudioEnabled(!audioEnabled)}
                        data-testid="audio-toggle"
                    >
                        {audioEnabled ? <Volume2 className="w-5 h-5" /> : <VolumeX className="w-5 h-5" />}
                    </IconButton>
                </div>
                
                {/* Progress bar */}
                <div className="mt-4 h-1.5 bg-[#B2DFDB] rounded-full overflow-hidden">
                    <div 
                        className="h-full bg-[#1D6B6B] rounded-full transition-all duration-500"
                        style={{ width: `${((currentStep + 1) / exercise.steps.length) * 100}%` }}
                    />
                </div>
            </header>

            <main className="max-w-md mx-auto px-6 py-8">
                {/* Step counter */}
                <div className="text-center mb-6">
                    <SmallText className="text-[#1D6B6B] font-medium">
                        Step {currentStep + 1} of {exercise.steps.length}
                    </SmallText>
                </div>

                {/* Instruction card */}
                <div className="bg-white rounded-2xl p-6 mb-6 shadow-sm">
                    <BodyText className="text-[#1D6B6B] leading-relaxed mb-4 text-lg">
                        {step.instruction}
                    </BodyText>
                    <div className="border-t border-white/40 pt-4">
                        <SmallText className="text-[#7D8E95] italic leading-relaxed">
                            {step.guidance}
                        </SmallText>
                    </div>
                </div>

                {/* Audio button */}
                {audioEnabled && (
                    <div className="text-center mb-6">
                        <button
                            onClick={generateAudio}
                            disabled={isLoadingAudio}
                            className="inline-flex items-center gap-2 px-5 py-2.5 bg-white/90 backdrop-blur-sm text-[#1D6B6B] rounded-xl shadow-soft hover:bg-white transition-all duration-300 disabled:opacity-50"
                        >
                            {isLoadingAudio ? (
                                <div className="w-4 h-4 border-2 border-[#1D6B6B] border-t-transparent rounded-full animate-spin" />
                            ) : (
                                <Play className="w-4 h-4 text-[#1D6B6B]" />
                            )}
                            {isLoadingAudio ? 'Generating...' : 'Play Audio'}
                        </button>
                    </div>
                )}

                {/* Navigation */}
                <div className="flex justify-between items-center">
                    <SecondaryButton
                        onClick={handlePrev}
                        disabled={currentStep === 0}
                        className="opacity-100 disabled:opacity-30"
                    >
                        <ChevronLeft className="w-4 h-4 mr-1" />
                        Back
                    </SecondaryButton>

                    {currentStep === exercise.steps.length - 1 ? (
                        <PrimaryButton onClick={() => navigate('/home')} className="bg-[#1D6B6B] hover:bg-[#00695C]">
                            Finish
                        </PrimaryButton>
                    ) : (
                        <PrimaryButton onClick={handleNext} className="bg-[#1D6B6B] hover:bg-[#00695C]">
                            Next
                            <ChevronRight className="w-4 h-4 ml-1" />
                        </PrimaryButton>
                    )}
                </div>

                {/* Step indicators */}
                <div className="flex justify-center gap-2 mt-8">
                    {exercise.steps.map((_, index) => (
                        <div
                            key={index}
                            className={`w-2 h-2 rounded-full transition-all ${
                                index === currentStep 
                                    ? 'bg-[#1D6B6B] scale-125' 
                                    : index < currentStep 
                                        ? 'bg-[#1D6B6B]/50' 
                                        : 'bg-[#B2DFDB]'
                            }`}
                        />
                    ))}
                </div>
            </main>
        </div>
    );
};

export default SomaticExperiencing;
